import React, { useState, useCallback, useMemo } from 'react';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';

import SimpleAggregation from 'components/AggregationCards/SimpleAggregation';
import AppBar from 'components/AppBar';
import ApprovalCardType from 'components/ApprovalCardType';
import Button from 'components/Buttons/Button';
import ProgressSteps from 'components/ProgressSteps';
import { Desktop, Mobile } from 'components/Responsive';
import { WizardProps } from 'components/Wizard';
import useStoreTranslation from 'hooks/useStoreTranslation';

import {
  Container,
  TransitionContainer,
  FragmentContainer,
  PaddingContainer,
  Header,
  Subtitle,
  BottomBackground,
  BottomBackgroundDesktop,
  GroupButton,
  InfoContainer,
  AggregationIcon,
} from '../../styles';

interface Props {
  wizard: WizardProps;
}

const ApprovalOnboarding = ({ wizard }: Props) => {
  const { t } = useStoreTranslation();

  const listApprovalTypes = useMemo(
    () => [
      {
        icon: '/corporate/images/eye.svg',
        title: t('approvalOnboardingForm.titleSuper'),
        description: t('approvalOnboardingForm.descriptionSuper'),
        type: 'PARALLEL',
        isSelected: false,
      },
      {
        icon: '/corporate/images/group-check.svg',
        title: t('approvalOnboardingForm.titleStrict'),
        description: t('approvalOnboardingForm.descriptionStrict'),
        type: 'SIMPLE',
        isSelected: false,
      },
    ],
    [t],
  );

  const [isSelectType, setIsSelectType] = useState(true);

  const [hasSelected, setHasSelected] = useState(() => {
    if (wizard.data.approvalType) {
      return true;
    }
    return false;
  });

  const [approvalTypes, setApprovalTypes] = useState(() => {
    let verifyApprovalType = wizard.data.approvalType;

    if (verifyApprovalType === 'SEQUENTIAL') {
      verifyApprovalType = 'PARALLEL';
    }

    if (verifyApprovalType) {
      return listApprovalTypes.map((approval) => {
        if (approval.type === verifyApprovalType) {
          return {
            ...approval,
            isSelected: true,
          };
        }
        return approval;
      });
    }
    return listApprovalTypes;
  });

  const handleSelectType = useCallback(
    (value: string) => {
      const selectList = listApprovalTypes.map((approval) => {
        if (approval.type === value) {
          return {
            ...approval,
            isSelected: true,
          };
        }
        return approval;
      });

      setApprovalTypes(selectList);
      setHasSelected(true);
      wizard.setData({
        ...wizard.data,
        approvalType: value,
      });
    },
    [wizard, listApprovalTypes],
  );

  const renderMainContainer = () => {
    if (isSelectType) {
      return (
        <FragmentContainer key="select-state">
          <PaddingContainer>
            <Header>
              <h3>{t('approvalOnboardingForm.headerSelectState')}</h3>
            </Header>
            {approvalTypes.map((approval) => (
              <ApprovalCardType
                type={approval.type}
                description={approval.description}
                icon={approval.icon}
                title={approval.title}
                isSelected={approval.isSelected}
                onClick={handleSelectType}
                key={approval.type}
              />
            ))}
            <InfoContainer type="leftTop">
              <p>{t('approvalOnboardingForm.infoSelectState')}</p>
            </InfoContainer>
          </PaddingContainer>
          {hasSelected && (
            <>
              <GroupButton>
                <Button
                  onClick={() => {
                    setIsSelectType(false);
                  }}
                  disabled={false}
                  value={t('approvalOnboardingForm.nextButton')}
                />
              </GroupButton>
            </>
          )}
        </FragmentContainer>
      );
    }

    return (
      <FragmentContainer key="default-state">
        <PaddingContainer>
          <Header>
            <h3>{t('approvalOnboardingForm.header')}</h3>
            <Subtitle>{t('approvalOnboardingForm.subtitle')}</Subtitle>
          </Header>
          <SimpleAggregation
            icon="primary-approval"
            rightIcon={<AggregationIcon />}
            placeholder={t('approvalOnboardingForm.aggregate')}
            onClick={wizard.nextStep}
          />
        </PaddingContainer>

        <Desktop>
          <BottomBackgroundDesktop>
            <BottomBackgroundDesktop.Description>
              {t('approvalOnboardingForm.bottomDescription')}
            </BottomBackgroundDesktop.Description>
            <BottomBackgroundDesktop.Image>
              <img alt="onboarding-approval" src="/corporate/images/onboarding_approval.svg" />
            </BottomBackgroundDesktop.Image>
          </BottomBackgroundDesktop>
        </Desktop>
        <Mobile>
          <BottomBackground>
            <BottomBackground.Image>
              <img alt="onboarding-approval" src="/corporate/images/onboarding_approval.svg" />
            </BottomBackground.Image>
            <BottomBackground.Description>{t('approvalOnboardingForm.bottomDescription')}</BottomBackground.Description>
          </BottomBackground>
        </Mobile>
      </FragmentContainer>
    );
  };

  return (
    <Container>
      <nav>
        <AppBar
          goBack={() => {
            if (!isSelectType) {
              setIsSelectType(true);
            } else {
              wizard.previousStep();
            }
          }}
        >
          {t('approvalOnboardingForm.title')}
        </AppBar>
        <ProgressSteps size={wizard.size} currentStep={wizard.currentStep} />
      </nav>
      <TransitionContainer>
        <ReactCSSTransitionGroup transitionName="fade">{renderMainContainer()}</ReactCSSTransitionGroup>
      </TransitionContainer>
    </Container>
  );
};

ApprovalOnboarding.defaultProps = {
  wizard: {},
};

export default ApprovalOnboarding;
